'use strict';

define('vb/private/translations/translationContext',[
  'ojs/ojtranslation',
  'vb/private/constants',
  'vb/private/log',
  'vb/private/utils',
], (
  ojTranslation,
  Constants,
  Log,
  Utils,
) => {
  /** @typedef {import('vb/private/translations/bundleUtils').BundlesModel} BundlesModel */
  /** @typedef {import('vb/private/stateManagement/container').default} Container */

  const logger = Log.getLogger('/vb/private/translations/translationContext');

  /**
   *
   */
  class TranslationContext {
    constructor() {
      throw new Error('TranslationContext is a set of static methods, do not call constructor');
    }

    /**
     * This callback is displayed as a global member.
     * @callback getBundleDefinition
     * @param {string} bundleName
     */

    /**
     * Define a 'format' function on stringMap. Use getBundleDefinition to get the appropriate bundle definition
     * to use.
     * @param {getBundleDefinition} getBundleDefinition get the bundle definition for bundles in stringMap
     * @param {Object} stringMap bundles string map
     * @returns {Object} string map with consolidated format() function
     */
    static defineFormatFunction(getBundleDefinition, stringMap) {
      // Hold on to the bundle named 'format' (if there is one)
      // Don't get the value, get the descriptor because it may be a getter that resolves to a proxy
      const formatPD = Object.getOwnPropertyDescriptor(stringMap, 'format');

      // (Re)define the format() function
      Object.defineProperty(stringMap, 'format', {
        get: () => (bundleName, key, ...params) => {
          // if no bundleName is passed, assume they are referencing a bundle named 'format'
          if (typeof bundleName === 'undefined') {
            if (!formatPD) {
              logger.error('Translation bundle "format" is not defined');
              return '';
            }
            // Could be proxy with a get() or a resolved value
            return formatPD.value ? formatPD.value : formatPD.get();
          }

          // delegate formatting to the actual instance (of BundleDefinition/BundleV2Definition).
          const bundleDef = getBundleDefinition(bundleName);
          if (!bundleDef) {
            logger.error(`Translation bundle "${bundleName}" is not defined`);
            return '';
          }
          return bundleDef.format(key, ...params);
        },
        enumerable: true,
      });

      return stringMap;
    }

    /**
     * Merge new string map with an existing string map which already has a 'format' function defined.
     * Redefine the 'format' function on the merged string map to also check the new string map.
     * Use getBundleDefinition to get the appropriate bundle definition in stringMap to use.
     * @param {Object} existingStringMap string map with 'format' function already defined.
     * @param {getBundleDefinition} getBundleDefinition get the bundle definition for bundles in stringMap
     * @param {Object} stringMap new bundles string map
     * @returns {Object} merged string map with consolidated format() function
     */
    static mergeAndDefineFormatFunction(existingStringMap, getBundleDefinition, stringMap) {
      const mergedStringMap = Object.assign({}, existingStringMap, stringMap);

      // Hold on to the existing 'format' function.
      // Don't get the value, get the descriptor because it may be a getter that resolves to a proxy
      const existingFormatPD = Object.getOwnPropertyDescriptor(existingStringMap, 'format');

      // Hold on to the bundle named 'format' (if there is one) in the new stringMap
      // Don't get the value, get the descriptor because it may be a getter that resolves to a proxy
      const formatPD = Object.getOwnPropertyDescriptor(stringMap, 'format');

      // (Re)define the format() function
      Object.defineProperty(mergedStringMap, 'format', {
        get: () => (bundleName, key, ...params) => {
          // if no bundleName is passed, assume they are referencing a bundle named 'format'
          if (typeof bundleName === 'undefined') {
            if (formatPD) {
              // If 'format' bundle is defined in stringMap, return it.  Otherwise delegate to existingStringMap.
              // Could be proxy with a get() or a resolved value
              return formatPD.value ? formatPD.value : formatPD.get();
            }
          } else {
            // delegate formatting to the actual instance (of BundleDefinition/BundleV2Definition).
            const bundleDef = getBundleDefinition(bundleName);
            if (bundleDef) {
              return bundleDef.format(key, ...params);
            }
          }

          // No bundle definition was found, so delegate formatting to the previous format() function that
          // was created in defineFormatFunction() above.
          return existingFormatPD.get()(bundleName, key, ...params);
        },
        enumerable: true,
      });

      return mergedStringMap;
    }

    /**
     * Create an object with just properties we want to expose to the expression context
     * Only the V1 translations bundles are included in this context.
     * Add a format() function to programmatically retrieve translated strings.
     * @param {BundlesModel} bundlesModel a container's bundles
     * @returns {Object}
     */
    static createV1Context(bundlesModel) {
      // if no bundles yet, just return the utility method(s), and don't look up the key
      // just in case this is called before the bundles are loaded, which shouldn't happen
      if (!bundlesModel) {
        return {
          format: (_, str, params) => ojTranslation.applyParameters(str, params),
        };
      }

      const stringMap = bundlesModel.getStringMap();

      // Define 'format' function for V1 Bundle Definition
      const getBundleDefinition = (bundleName) => bundlesModel.getBundleDefinition(bundleName);
      return TranslationContext.defineFormatFunction(getBundleDefinition, stringMap);
    }

    /**
     * Create an object with just properties we want to expose to the expression context
     * Both V1 and V2 translations bundles are included in this context.
     * Add a format() function to programmatically retrieve translated strings.
     * @param {BundlesModel} bundlesModel a container's bundles
     * @returns {Object}
     */
    static createContext(bundlesModel) {
      // if no bundles yet, just return the utility method(s), and don't look up the key
      // just in case this is called before the bundles are loaded, which shouldn't happen.
      if (!bundlesModel) {
        return {
          format: (_, str, params) => ojTranslation.applyParameters(str, params),
        };
      }

      let stringMap = bundlesModel.getStringMap();
      stringMap = bundlesModel.getV2StringMap(stringMap);

      // Define 'format' function for V1 or V2 Bundle Definition.
      // V2 takes precedence in format(), just like in string map.
      const getBundleDefinition = (bundleName) => bundlesModel.getV2BundleDefinition(bundleName)
        || bundlesModel.getBundleDefinition(bundleName);
      return TranslationContext.defineFormatFunction(getBundleDefinition, stringMap);
    }

    /**
     * Add $extension.<extId>.translations.<bundleName> for all extension translation bundles referenced by the
     * bundlesModel (i.e. translation bundles in other extensions)
     * @param {BundlesModel} bundlesModel a container's bundles
     * @param extensionsContext {Object} extensions context to add the <extId>.translations contexts
     */
    static addExtensionsContexts(bundlesModel, extensionsContext) {
      const extensionsBundles = Object.entries(bundlesModel.getExtensionsV2StringMaps());
      if (extensionsBundles.length) {
        extensionsBundles.forEach(([extId, stringMap]) => {
          if (!extensionsContext[extId]) {
            // If there isn't already an extension entry for extId, create one
            const extensionContext = {};
            Object.defineProperty(extensionsContext, extId, {
              enumerable: true,
              configurable: true,
              value: extensionContext,
            });
          }

          // Add the bundles from stringMap into the extensionContext
          TranslationContext.updateExtensionContext(bundlesModel, extensionsContext[extId], extId, stringMap);
        });
      }
    }

    /**
     * Add $base.translations
     * If there is "imports"/"translations"/"base" in the .json, it is added as $base.translations.
     * @param {Container} container appUi, page, flow, etc that declares the translations
     * @param {Object} extensionContext extension context to add the $base translations property
     */
    static addBaseExtensionContexts(container, extensionContext) {
      const stringMap = container.bundles.getExtensionV2StringMap(Constants.ExtensionFolders.BASE);
      if (Object.keys(stringMap).length) {
        TranslationContext.updateExtensionContext(container.bundles, extensionContext, Constants.ExtensionFolders.BASE,
          stringMap);
      }
    }

    /**
     * Update the ExtensionContext (which may be $base or a $extension.<extId>) with the bundles in stringMap.
     * @param {BundlesModel} bundlesModel
     * @param {Object} extensionContext extension context to add the translations property
     * @param {string} extId id of extension to get V2 bundles from.
     * @param {Object} stringMap bundles string map
     */
    static updateExtensionContext(bundlesModel, extensionContext, extId, stringMap) {
      // Define 'format' function for Extension V2 Bundle Definition
      const getBundleDefinition = (bundleName) => bundlesModel.getExtensionV2BundleDefinition(extId, bundleName);

      let translationsStringMap;
      if (extensionContext.translations) {
        // If there is already a 'translations' property defined, merge the imported translation bundles
        translationsStringMap = TranslationContext.mergeAndDefineFormatFunction(extensionContext.translations,
          getBundleDefinition, stringMap);
      } else {
        translationsStringMap = TranslationContext.defineFormatFunction(getBundleDefinition, stringMap);
      }

      // [Re]define $extension.<extId>.translations / $base.translations to include the imported translation bundles
      TranslationContext.defineTranslationsProperty(extensionContext, Constants.TRANSLATIONS_CONTEXT,
        translationsStringMap);
    }

    /**
     * Define the 'translations' (or '$translations') property on the context to return the value.
     * This can be used to re-write a translations property so the context object is only constructed when
     * requested.
     * It is marked writeable if running in action chain tester mode.
     * @param {Object} context context to add the translations property
     * @param {string} propertyName 'translations' or '$translations'
     * @param {Object} value translations bundle map
     */
    static defineTranslationsProperty(context, propertyName, value) {
      Object.defineProperty(context, propertyName, {
        value,
        enumerable: true,
        configurable: true,
        // make it writable under action chain tester mode
        writable: Utils.isActionChainTestMode(),
      });
    }

    /**
     * Construct a container's 'translations' property descriptor for V1 strings.
     * This is a self updating property descriptor: the bundles context is loaded on the first get, and then
     * the property descriptor rewrites itself with that value so it doesn't have to be calculated again.
     *
     * We expose V2 Strings only in the $translations EL context (as well as V1 Strings).
     *
     * @param {Object} context context to add the translations property
     * @param {Container} container appUi, page, flow, etc that declares the translations
     * @returns {PropertyDescriptor}
     */
    static createV1TranslationsPropertyDescriptor(context, container) {
      const propertyDescriptor = {
        /**
         * Accessor for the translations
         * The container context .translations exposes the V1 Strings from the BundlesModel.
         * We expose V2 Strings only in the $translations EL context (as well as V1 Strings)
         * @type {Object}
         */
        get() {
          // (check container: In unit tests, I've seen container not defined)
          const bundleContext = container ? TranslationContext.createV1Context(container.bundles) : {};

          // Redefine the property as a value for immediate access on the next get.
          TranslationContext.defineTranslationsProperty(context, Constants.TRANSLATIONS_CONTEXT, bundleContext);

          return bundleContext;
        },

        /**
         * Setter used by action chain tester to mock translation bundles.
         *
         * @param {Object} value mock bundle
         */
        set(value) {
          // only override if we are in action chain test mode
          if (Utils.isActionChainTestMode()) {
            TranslationContext.defineTranslationsProperty(context, Constants.TRANSLATIONS_CONTEXT, value);
          }
        },

        enumerable: true,
        configurable: true,
      };

      return propertyDescriptor;
    }

    /**
     * Construct a container's '$translations' property descriptor.
     * This is a self updating property descriptor: the bundles context is loaded on the first get, and then
     * the property descriptor rewrites itself with that value so it doesn't have to be calculated again.
     *
     * @param {Object} context context to add the translations property
     * @param {Container} container appUi, page, flow, etc that declares the translations
     * @returns {PropertyDescriptor}
     */
    static createTranslationsPropertyDescriptor(context, container) {
      const propertyDescriptor = {
        /**
         * Accessor for the translations
         * @type {Object}
         */
        get() {
          // (check container: In unit tests, I've seen container not defined)
          const bundleContext = container ? TranslationContext.createContext(container.bundles) : {};

          // Redefine the property as a value for immediate access on the next get.
          TranslationContext.defineTranslationsProperty(context, Constants.TRANSLATIONS_CONTEXT, bundleContext);

          return bundleContext;
        },

        /**
         * Setter used by action chain tester to mock translation bundles.
         *
         * @param {Object} value mock bundle
         */
        set(value) {
          // only override if we are in action chain test mode
          if (Utils.isActionChainTestMode()) {
            TranslationContext.defineTranslationsProperty(context, Constants.TRANSLATIONS_CONTEXT, value);
          }
        },

        enumerable: true,
        configurable: true,
      };

      return propertyDescriptor;
    }
  }

  // all static
  return TranslationContext;
});

